import React, { useEffect, useImperativeHandle, useState, forwardRef } from 'react';
import {
    Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography
} from '@mui/material';
import httpClient, { extractErrorMessage } from '../Services/httpClient';

const formatDateTime = (timestamp) => new Date(timestamp).toLocaleString('pt-BR');

// Solicitações acima do limite de auto-aprovação (core-wallet CoinGrantOptions.ApprovalThreshold)
// caem aqui. Exposto via ref.reload() pra CoinGrantsPanel atualizar a lista depois de criar um
// novo grant sem precisar levantar todo o estado pro componente pai.
const CoinGrantPendingPanel = forwardRef((_props, ref) => {
    const [items, setItems] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [rejectTarget, setRejectTarget] = useState(null);
    const [rejectReason, setRejectReason] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);

    const load = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await httpClient.get('/backoffice/coin-grants/pending');
            setItems(response.data);
        } catch (err) {
            setError(extractErrorMessage(err));
        } finally {
            setIsLoading(false);
        }
    };

    useImperativeHandle(ref, () => ({ reload: load }));

    useEffect(() => {
        load();
    }, []);

    const handleApprove = async (id) => {
        setError(null);
        setSuccess(null);
        try {
            await httpClient.post(`/backoffice/coin-grants/${id}/approve`);
            setSuccess('Concessão aprovada.');
            await load();
        } catch (err) {
            setError(extractErrorMessage(err));
        }
    };

    const handleOpenReject = (id) => {
        setRejectTarget(id);
        setRejectReason('');
    };

    const handleConfirmReject = async () => {
        if (!rejectTarget || !rejectReason.trim()) return;
        setIsProcessing(true);
        setError(null);
        setSuccess(null);
        try {
            await httpClient.post(`/backoffice/coin-grants/${rejectTarget}/reject`, rejectReason, {
                headers: { 'Content-Type': 'application/json' }
            });
            setSuccess('Concessão rejeitada.');
            setRejectTarget(null);
            await load();
        } catch (err) {
            setError(extractErrorMessage(err));
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.secondary', mb: 1 }}>
                Pendentes de aprovação {items.length > 0 && `(${items.length})`}
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 1 }}>{success}</Alert>}

            {isLoading ? (
                <CircularProgress size={22} style={{ color: '#f77513' }} />
            ) : items.length === 0 ? (
                <Typography variant="body2" color="text.secondary">Nenhuma concessão pendente.</Typography>
            ) : (
                <TableContainer variant="outlined" component={Box} sx={{ borderRadius: '10px', border: '1px solid #e0e0e0' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow style={{ backgroundColor: '#fff4ec' }}>
                                <TableCell>Consultor</TableCell>
                                <TableCell align="right">Qtde</TableCell>
                                <TableCell>Motivo</TableCell>
                                <TableCell>Descrição</TableCell>
                                <TableCell>Criado por</TableCell>
                                <TableCell>Data</TableCell>
                                <TableCell align="right">Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.map((item) => (
                                <TableRow key={item.id}>
                                    <TableCell style={{ fontFamily: 'monospace', fontSize: '12px' }}>{item.consultantId}</TableCell>
                                    <TableCell align="right">{item.quantity} 🍋</TableCell>
                                    <TableCell>{item.reasonCode}</TableCell>
                                    <TableCell>{item.description}</TableCell>
                                    <TableCell>{item.createdBy}</TableCell>
                                    <TableCell>{formatDateTime(item.createdAt)}</TableCell>
                                    <TableCell align="right">
                                        <Button size="small" style={{ color: '#2e7d32' }} onClick={() => handleApprove(item.id)}>
                                            Aprovar
                                        </Button>
                                        <Button size="small" style={{ color: '#c62828' }} onClick={() => handleOpenReject(item.id)}>
                                            Rejeitar
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog open={Boolean(rejectTarget)} onClose={isProcessing ? undefined : () => setRejectTarget(null)} fullWidth maxWidth="xs">
                <DialogTitle>Rejeitar concessão</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>Informe o motivo da rejeição:</Typography>
                    <TextField
                        label="Motivo"
                        fullWidth
                        multiline
                        minRows={2}
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        autoFocus
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRejectTarget(null)} disabled={isProcessing} style={{ color: '#666' }}>Cancelar</Button>
                    <Button
                        onClick={handleConfirmReject}
                        disabled={isProcessing || !rejectReason.trim()}
                        variant="contained"
                        color="error"
                        startIcon={isProcessing ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                        Confirmar rejeição
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
});

export default CoinGrantPendingPanel;
